import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import type { BaseReport, TimeRange } from '@xweather/protect-types'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { I18n } from '../../components/I18n.tsx'
import { Stack } from '../../components/Stack.tsx'
import { usePreferences } from '../../data/userProfile.ts'
import { dayjs } from '../../dayjs.ts'
import { useDistanceRangeHelpers } from '../distance-range-helpers.ts'
import { getTimezoneLabel } from '../preferences/timezones.ts'
import { type DateTimeFormat, useFormatDate } from '../preferences/useFormatDate.tsx'
import { useReportLocations } from './useReportLocations.ts'

const MAX_LISTED_LOCATIONS = 3

export const ReportSummaryHeader = ({
	assetVrns,
	radiusKm,
	customPoint,
	timezone,
	timeRange,
	precision,
	areaDetails,
	children,
}: {
	assetVrns: BaseReport['assetVrns']
	radiusKm: number
	customPoint: BaseReport['customPoint']
	timezone: string
	timeRange?: TimeRange
	precision?: 'minute'
	areaDetails?: ReactNode
	children?: ReactNode
}) => {
	const { t } = useTranslation()
	const formatDate = useFormatDate()
	const distanceUnit = usePreferences().data?.unitsDistance ?? 'KILOMETRES'
	const { fromKmToUserPreference } = useDistanceRangeHelpers()
	const locations = useReportLocations().locationsOf(assetVrns)
	const names = locations.map(({ name }) => name)
	const hiddenCount = names.length - MAX_LISTED_LOCATIONS
	const listedNames =
		hiddenCount > 0 ? [...names.slice(0, MAX_LISTED_LOCATIONS), t(($) => $.reports.summaryBanner.moreLocations, { hiddenCount })] : names

	const dateFormat = ((): DateTimeFormat => {
		if (precision === 'minute') return 'DD.MM.YYYY.HH.mm'
		const hasMilliseconds = timeRange && (dayjs(timeRange.start).millisecond() !== 0 || dayjs(timeRange.end).millisecond() !== 0)
		return hasMilliseconds ? 'DD.MM.YYYY.HH.mm.ss.SSS' : 'DD.MM.YYYY.HH.mm.ss'
	})()

	return (
		<Stack col spacing={2}>
			<Tooltip title={hiddenCount > 0 ? names.join(', ') : ''}>
				<Typography variant='h2'>
					<I18n
						i18nKey={($) => $.reports.summaryBanner.selectedArea}
						values={{
							radius: fromKmToUserPreference(radiusKm, 2),
							unit: t(($) => $.preferences.unitLabels[distanceUnit]),
							assetName: listedNames.join(', '),
							formattedCustomPoint: customPoint
								? ` (${customPoint.coordinates[1].toFixed(5)}, ${customPoint.coordinates[0].toFixed(5)})`
								: '',
						}}
					/>
					{areaDetails && (
						<>
							<br />
							{areaDetails}
						</>
					)}
				</Typography>
			</Tooltip>
			{timeRange && (
				<Stack align='center' flexWrap='wrap' rowGap={0}>
					<Typography variant='body1'>
						{formatDate(timeRange.start, dateFormat, timezone)} - {formatDate(timeRange.end, dateFormat, timezone)}
					</Typography>
					<TimezoneLabel times={[timeRange.start, timeRange.end]} timezone={timezone} />
				</Stack>
			)}
			{children}
		</Stack>
	)
}

export const TimezoneLabel = ({ times, timezone }: { times: string[]; timezone: string }) => {
	const formatDate = useFormatDate()
	const utcOffsets = Array.from(new Set(times.map((time) => formatDate(time, 'Z', timezone))))
	const utcOffsetsElement = timezone === 'UTC' ? null : `(${utcOffsets.map((offset) => `UTC${offset}`).join(', ')}) `

	return (
		<Typography variant='body2' color='textSecondary'>
			{utcOffsetsElement}
			{getTimezoneLabel(timezone)}
		</Typography>
	)
}
